import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal,
    Box,
    Text,
    Textarea,
    ActionIcon,
    Divider,
    Switch,
} from '@mantine/core';
import { Button } from '@xplatform/design-system';
import { IconX, IconInfoCircle } from '@tabler/icons-react';
import type { ExtractionOptions } from '@src/hooks/extraction/useExtraction';
import { useExtractionForm } from '@hooks/extraction/useExtractionForm';
import { useUserInstructions } from '@hooks/extraction/useUserInstructions';
import { useDoctypeMappingConfig } from '@hooks/extraction/useDoctypeMapping';
import FilePreview from './FilePreview';
import PageNumbersSection from './PageNumbersSection';
import InstructionSelector, { NEW_INSTRUCTION_VALUE } from './InstructionSelector';
import InstructionsHelpDialog from './InstructionsHelpDialog/InstructionsHelpDialog';
import './ExtractionOption.scss';

interface ExtractionOptionProps {
    open: boolean;
    onClose: () => void;
    onSave: (options: ExtractionOptions) => void;
    initialOptions?: ExtractionOptions;
    uploadedFile?: File;
    doctypeCode?: string;
}

const ExtractionOption: React.FC<ExtractionOptionProps> = ({
    open,
    onClose,
    onSave,
    initialOptions,
    uploadedFile,
    doctypeCode,
}) => {
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);
    const [selectedInstructionId, setSelectedInstructionId] = useState<string | null>(null);
    const [instructionName, setInstructionName] = useState('');
    const [nameError, setNameError] = useState('');
    // Per-run mapping opt-in. Only offered when the doctype has an enabled
    // mapping_config; defaults off.
    const mappingConfig = useDoctypeMappingConfig(doctypeCode);
    const mappingAvailable = Boolean(mappingConfig?.enabled);
    const [mappingEnabled, setMappingEnabled] = useState(
        initialOptions?.mappingEnabled ?? false,
    );

    const {
        pageNumbersInput,
        pageNumbers,
        instructions,
        numPages,
        pageNumbersError,
        instructionsError,
        isDebouncing,
        countdown,
        fileKind,
        isFormValid,
        handlePageNumbersChange,
        handleInstructionsChange,
        onDocumentLoadSuccess,
        validateForm,
        getFormData,
    } = useExtractionForm({ initialOptions, uploadedFile });

    const {
        instructions: savedInstructions,
        isLoading: isLoadingInstructions,
        isSaving,
        load: loadInstructions,
        save: saveInstruction,
        update: updateInstruction,
        remove: removeInstruction,
    } = useUserInstructions(doctypeCode ?? '');

    useEffect(() => {
        if (open && doctypeCode) {
            loadInstructions();
            setSelectedInstructionId(null);
            setInstructionName('');
            setNameError('');
        }
    }, [open, doctypeCode, loadInstructions]);

    const isNewInstruction = selectedInstructionId === NEW_INSTRUCTION_VALUE;
    const isExistingInstruction =
        selectedInstructionId !== null && selectedInstructionId !== NEW_INSTRUCTION_VALUE;

    const selectedSaved = isExistingInstruction
        ? savedInstructions.find((i) => i.id === selectedInstructionId)
        : null;
    const isInstructionDirty = selectedSaved
        ? instructions.trim() !== selectedSaved.instruction.trim()
            || instructionName.trim() !== selectedSaved.name.trim()
        : false;

    const handleSaveOptions = () => {
        if (!validateForm()) return;
        onSave({
            ...getFormData(),
            mappingEnabled: mappingAvailable ? mappingEnabled : false,
        });
        onClose();
    };

    const handleSelectChange = useCallback(
        (value: string | null) => {
            setNameError('');
            if (value === NEW_INSTRUCTION_VALUE) {
                setSelectedInstructionId(NEW_INSTRUCTION_VALUE);
                setInstructionName('');
                handleInstructionsChange({
                    target: { value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
                return;
            }
            if (!value) {
                setSelectedInstructionId(null);
                setInstructionName('');
                return;
            }
            const found = savedInstructions.find((i) => i.id === value);
            if (found) {
                setSelectedInstructionId(found.id);
                setInstructionName(found.name);
                handleInstructionsChange({
                    target: { value: found.instruction },
                } as React.ChangeEvent<HTMLInputElement>);
            }
        },
        [savedInstructions, handleInstructionsChange],
    );

    const handleNameChange = (value: string) => {
        setInstructionName(value);
        if (nameError) setNameError('');
    };

    const validateName = (): boolean => {
        const name = instructionName.trim();
        if (!name) {
            setNameError('Name is required');
            return false;
        }
        if (name.length > 50) {
            setNameError('Name must be 50 characters or less');
            return false;
        }
        const duplicate = savedInstructions.find(
            (i) =>
                i.name.toLowerCase() === name.toLowerCase() &&
                i.id !== selectedInstructionId,
        );
        if (duplicate) {
            setNameError('An instruction with this name already exists');
            return false;
        }
        return true;
    };

    const handleSaveInstruction = async () => {
        if (!validateName()) return;
        if (!instructions.trim()) {
            setNameError('Instruction content cannot be empty');
            return;
        }
        if (isNewInstruction) {
            const result = await saveInstruction(instructionName.trim(), instructions.trim());
            if (result) {
                setSelectedInstructionId(result.id);
                setNameError('');
            }
        } else if (isExistingInstruction) {
            const result = await updateInstruction(
                selectedInstructionId,
                instructionName.trim(),
                instructions.trim(),
            );
            if (result) setNameError('');
        }
    };

    const handleDeleteInstruction = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const success = await removeInstruction(id);
        if (success && selectedInstructionId === id) {
            setSelectedInstructionId(null);
            setInstructionName('');
            handleInstructionsChange({
                target: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>);
        }
    };

    return (
        <>
            <Modal
                opened={open}
                onClose={onClose}
                withCloseButton={false}
                size="auto"
                centered
                className="extraction-option-modal"
                overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
                portalProps={{ target: '#extraction-portal-root' }}
            >
                <div className="modal-content">
                    <Box className="modal-header">
                        <Text size="lg" fw={600} component="h2">
                            Extraction Options
                        </Text>
                        <ActionIcon onClick={onClose} size="sm" variant="subtle" aria-label="Close">
                            <IconX className="icon-primary" size={16} />
                        </ActionIcon>
                    </Box>

                    <Divider />

                    <Box className="modal-body">
                        <div className="options-column">
                            <PageNumbersSection
                                fileKind={fileKind}
                                pageNumbersInput={pageNumbersInput}
                                pageNumbersError={pageNumbersError}
                                isDebouncing={isDebouncing}
                                countdown={countdown}
                                onPageNumbersChange={handlePageNumbersChange}
                            />

                            <Box className="option-section option-section--grow">
                                <Box className="section-title-with-icon">
                                    <Text size="sm" fw={600} className="section-title">
                                        Instructions
                                    </Text>
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        onClick={() => setHelpDialogOpen(true)}
                                        aria-label="Instructions help"
                                    >
                                        <IconInfoCircle className="icon-info" size={14} />
                                    </ActionIcon>
                                    {doctypeCode && (
                                        <Box style={{ flex: 1 }} />
                                    )}
                                    {doctypeCode && (
                                        <InstructionSelector
                                            savedInstructions={savedInstructions}
                                            isLoading={isLoadingInstructions}
                                            isSaving={isSaving}
                                            selectedId={selectedInstructionId}
                                            instructionName={instructionName}
                                            nameError={nameError}
                                            instructionText={instructions}
                                            onSelectChange={handleSelectChange}
                                            onNameChange={handleNameChange}
                                            onSave={handleSaveInstruction}
                                            onDelete={handleDeleteInstruction}
                                            isNewInstruction={isNewInstruction}
                                            isExistingInstruction={isExistingInstruction}
                                            isInstructionDirty={isInstructionDirty}
                                            inline
                                        />
                                    )}
                                </Box>

                                {doctypeCode && (isNewInstruction || isExistingInstruction) && (
                                    <InstructionSelector
                                        savedInstructions={savedInstructions}
                                        isLoading={isLoadingInstructions}
                                        isSaving={isSaving}
                                        selectedId={selectedInstructionId}
                                        instructionName={instructionName}
                                        nameError={nameError}
                                        instructionText={instructions}
                                        onSelectChange={handleSelectChange}
                                        onNameChange={handleNameChange}
                                        onSave={handleSaveInstruction}
                                        onDelete={handleDeleteInstruction}
                                        isNewInstruction={isNewInstruction}
                                        isExistingInstruction={isExistingInstruction}
                                        isInstructionDirty={isInstructionDirty}
                                        nameOnly
                                    />
                                )}

                                <Textarea
                                    minRows={25}
                                    autosize
                                    placeholder="Provide specific instructions to guide AI in the extraction process."
                                    value={instructions}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                        handleInstructionsChange(
                                            e as unknown as React.ChangeEvent<HTMLInputElement>,
                                        )
                                    }
                                    error={instructionsError || undefined}
                                    className="instructions-field"
                                />
                            </Box>

                            {mappingAvailable && (
                                <Box mt="md">
                                    <Switch
                                        label="Enable mapping for this document"
                                        description="Send completed results to the configured mapping service and show mapping status."
                                        checked={mappingEnabled}
                                        onChange={(e) => setMappingEnabled(e.currentTarget.checked)}
                                    />
                                </Box>
                            )}
                        </div>

                        <div className="pdf-viewer-column">
                            <FilePreview
                                uploadedFile={uploadedFile}
                                pageNumbers={pageNumbers}
                                numPages={numPages}
                                onDocumentLoadSuccess={onDocumentLoadSuccess}
                            />
                        </div>
                    </Box>

                    <Divider />

                    <Box className="modal-footer">
                        <Button onClick={handleSaveOptions} disabled={!isFormValid}>
                            Save Options
                        </Button>
                    </Box>
                </div>
            </Modal>

            <InstructionsHelpDialog
                open={helpDialogOpen}
                onClose={() => setHelpDialogOpen(false)}
            />
        </>
    );
};

export default ExtractionOption;
